import { type GenericSequenceKey, genericSequenceMap } from '../constants/genericSequenceMap';
import { type Market, type Subevent } from '../interfaces/subevent-group.interface';

import { useMatchesCouponContext } from './useMatchesCouponContext';
import { useSubevents } from './useSubevents';

const findIndexWithMostMarkets = (subevents: Subevent[]) => {
  return subevents.reduce((maxIndex, subevent, i) => {
    const maxMarketsLength = subevents[maxIndex].markets?.length || 0;
    const currentMarketsLength = subevent.markets?.length || 0;
    return currentMarketsLength > maxMarketsLength ? i : maxIndex;
  }, 0);
};

const findBetOrMarketGenericSequence = (markets: Market[]): GenericSequenceKey | undefined => {
  for (const market of markets) {
    if (market.marketGenericSequence !== undefined) {
      return market.marketGenericSequence as GenericSequenceKey;
    }
  }
  return undefined;
};

const addBetTypeIdToNames = (names: string[], betTypeId: number) => {
  return names.map(name => ({ betTypeId, name }));
};

const extractNamesWithBetTypeId = (markets: Market[]) => {
  const betOrMarketNamesWithBetTypeIdDirectlyFromSubevents = markets.reduce(
    (betOrMarketNames, betOrMarket) => {
      if (betOrMarket.name) {
        betOrMarketNames.push({
          betTypeId: betOrMarket.betTypeId,
          name: betOrMarket.name,
        });
      }
      return betOrMarketNames;
    },
    [] as { betTypeId: number; name: string }[],
  );

  return betOrMarketNamesWithBetTypeIdDirectlyFromSubevents;
};

export const useMarketLabelsFromSubevent = () => {
  const subevents = useSubevents();
  const bettypeIds = useMatchesCouponContext(state => state.bettypeIds) || [];

  if (subevents.length === 0 || bettypeIds.length === 0) return [];

  const indexWithMostMarkets = findIndexWithMostMarkets(subevents);

  const betOrMarketGenericSequence = findBetOrMarketGenericSequence(subevents[indexWithMostMarkets].markets) || '';

  const moreThanOneBetTypeIdUseMarketNameAndBetTypeId =
    !(betOrMarketGenericSequence in genericSequenceMap) || bettypeIds.length > 1;

  if (moreThanOneBetTypeIdUseMarketNameAndBetTypeId) {
    const betOrMarketNamesWithBetTypeId = extractNamesWithBetTypeId(subevents[indexWithMostMarkets].markets);

    return betOrMarketNamesWithBetTypeId;
  }

  // If single betTypeId, use names from generic sequence map
  const betOrMarketNames = genericSequenceMap[betOrMarketGenericSequence as keyof typeof genericSequenceMap];
  const betTypeId = bettypeIds[0];

  const betOrMarketNamesWithBetTypeId = addBetTypeIdToNames(betOrMarketNames, betTypeId);

  return betOrMarketNamesWithBetTypeId;
};
